import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus,
  RefreshCw,
  Save,
  Trash2,
  X,
  Edit,
  ChevronRight,
  ChevronDown,
  Warehouse as WarehouseIcon,
  FolderTree,
} from 'lucide-react';
import MainService from '../../services/MainService';

const emptyWarehouse = () => ({
  Id: 0,
  Code: '',
  Name: '',
  NameAr: '',
  BranchCode: '',
  Address: '',
  ContactMobile: '',
  LAT: '',
  LNG: '',
});

const CHILD_TYPE = {
  WAREHOUSE: 'AREA',
  AREA: 'RACK',
  RACK: 'SHELF',
  SHELF: 'BIN',
};

const PARENT_TYPE = {
  AREA: null,
  RACK: 'AREA',
  SHELF: 'RACK',
  BIN: 'SHELF',
};

const TYPE_LABEL = {
  AREA: 'Area',
  RACK: 'Rack',
  SHELF: 'Shelf',
  BIN: 'Bin',
};

const TYPE_COLOR = {
  AREA: 'bg-emerald-100 text-emerald-800',
  RACK: 'bg-sky-100 text-sky-800',
  SHELF: 'bg-amber-100 text-amber-800',
  BIN: 'bg-violet-100 text-violet-800',
};

const emptyLoc = (locationType, warehouseId, parentId) => ({
  Id: 0,
  WarehouseId: Number(warehouseId) || 0,
  ParentId: parentId == null || parentId === '' ? null : Number(parentId),
  Code: '',
  Name: '',
  LocationType: locationType,
  SortOrder: 0,
});

function normalizeLoc(raw) {
  if (!raw) return null;
  let parentId = raw.ParentId ?? raw.parentId;
  if (parentId === 0 || parentId === '0' || parentId === '' || parentId === undefined) parentId = null;
  else parentId = Number(parentId);

  return {
    Id: Number(raw.Id ?? raw.id ?? 0),
    WarehouseId: Number(raw.WarehouseId ?? raw.warehouseId ?? 0),
    ParentId: parentId,
    Code: raw.Code ?? raw.code ?? '',
    Name: raw.Name ?? raw.name ?? '',
    LocationType: String(raw.LocationType ?? raw.locationType ?? '').toUpperCase(),
    FullPath: raw.FullPath ?? raw.fullPath ?? '',
    SortOrder: Number(raw.SortOrder ?? raw.sortOrder ?? 0),
    ChildCount: Number(raw.ChildCount ?? raw.childCount ?? 0),
  };
}

/** Build nested tree. Only AREA without parent is a true root; misplaced nodes → orphans. */
function buildTree(flatList) {
  const items = (flatList || []).map(normalizeLoc).filter((x) => x && x.Id);
  const byId = new Map(items.map((i) => [i.Id, i]));
  const byParent = new Map();

  const push = (key, item) => {
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(item);
  };

  const orphans = [];

  items.forEach((item) => {
    if (item.LocationType === 'AREA' && item.ParentId == null) {
      push('root', item);
      return;
    }
    if (item.ParentId != null && byId.has(item.ParentId)) {
      const parent = byId.get(item.ParentId);
      const expected = PARENT_TYPE[item.LocationType];
      if (expected && parent.LocationType === expected) {
        push(String(item.ParentId), item);
        return;
      }
    }
    // Rack/Shelf/Bin without valid parent
    orphans.push(item);
  });

  const sortFn = (a, b) =>
    (a.SortOrder || 0) - (b.SortOrder || 0) || String(a.Name || '').localeCompare(String(b.Name || ''));

  const attach = (parentId) => {
    const key = parentId == null ? 'root' : String(parentId);
    return (byParent.get(key) || [])
      .slice()
      .sort(sortFn)
      .map((c) => ({ ...c, children: attach(c.Id) }));
  };

  return { tree: attach(null), orphans: orphans.sort(sortFn) };
}

const WarehouseMaster = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [whForm, setWhForm] = useState(emptyWarehouse());
  const [whOpen, setWhOpen] = useState(false);

  const [locMap, setLocMap] = useState({});
  const [locLoadingId, setLocLoadingId] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  const [locForm, setLocForm] = useState(null);
  const [locOpen, setLocOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const flash = (type, msg) => {
    if (type === 'ok') {
      setSuccess(msg);
      setError('');
      setTimeout(() => setSuccess(''), 3500);
    } else {
      setError(msg);
      setSuccess('');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await MainService.WarehousesList({ Skip: 0, Take: 200 });
      setRows(resp?.Data || []);
    } catch (e) {
      console.error(e);
      flash('err', 'Failed to load warehouses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadLocations = async (warehouseId) => {
    setLocLoadingId(warehouseId);
    try {
      const list = await MainService.WarehouseLocationsByWarehouse(warehouseId);
      const flat = (Array.isArray(list) ? list : []).map(normalizeLoc).filter(Boolean);
      setLocMap((prev) => ({ ...prev, [warehouseId]: flat }));
      return flat;
    } catch (e) {
      console.error(e);
      flash('err', 'Failed to load locations. Check API + migration (ParentId column).');
      setLocMap((prev) => ({ ...prev, [warehouseId]: [] }));
      return [];
    } finally {
      setLocLoadingId(null);
    }
  };

  const toggleExpand = async (key, warehouseId) => {
    const willOpen = !expanded.has(key);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    if (willOpen && key.startsWith('wh:') && !locMap[warehouseId]) {
      await loadLocations(warehouseId);
    }
  };

  const ensureWarehouseExpanded = async (warehouseId) => {
    if (!locMap[warehouseId]) await loadLocations(warehouseId);
    setExpanded((prev) => {
      const next = new Set(prev);
      next.add(`wh:${warehouseId}`);
      return next;
    });
  };

  const saveWarehouse = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const resp = await MainService.SaveWarehouse(whForm);
      if (resp === 'Success') {
        setWhOpen(false);
        flash('ok', 'Warehouse saved');
        await load();
      } else flash('err', resp || 'Failed to save warehouse');
    } catch (err) {
      flash('err', err?.message || 'Failed to save warehouse');
    } finally {
      setSaving(false);
    }
  };

  const openAddChild = async (warehouseId, parentLoc) => {
    // Capture parent ids BEFORE any await (avoid stale/lost parent)
    const parentId = parentLoc ? Number(parentLoc.Id ?? parentLoc.id) : null;
    const parentType = String(parentLoc?.LocationType || parentLoc?.locationType || 'WAREHOUSE').toUpperCase();
    const locationType = parentLoc ? CHILD_TYPE[parentType] : 'AREA';
    if (!locationType) {
      flash('err', 'Cannot add child under this level');
      return;
    }
    if (locationType !== 'AREA' && !parentId) {
      flash('err', `${TYPE_LABEL[locationType]} must be added under a ${TYPE_LABEL[PARENT_TYPE[locationType]]}`);
      return;
    }

    await ensureWarehouseExpanded(warehouseId);
    setLocForm(emptyLoc(locationType, warehouseId, parentId));
    setLocOpen(true);
    if (parentId) {
      setExpanded((prev) => {
        const next = new Set(prev);
        next.add(`loc:${parentId}`);
        next.add(`wh:${warehouseId}`);
        return next;
      });
    }
  };

  const openEditLoc = (loc) => {
    const n = normalizeLoc(loc);
    setLocForm({
      Id: n.Id,
      WarehouseId: n.WarehouseId,
      ParentId: n.ParentId,
      Code: n.Code || '',
      Name: n.Name || '',
      LocationType: n.LocationType,
      SortOrder: n.SortOrder || 0,
    });
    setLocOpen(true);
  };

  const saveLoc = async (e) => {
    e.preventDefault();
    if (!locForm?.Name?.trim()) {
      flash('err', 'Name is required');
      return;
    }

    const type = String(locForm.LocationType || '').toUpperCase();
    let parentId = locForm.ParentId == null || locForm.ParentId === '' ? null : Number(locForm.ParentId);

    if (type === 'AREA') {
      parentId = null;
    } else {
      if (!parentId || Number.isNaN(parentId)) {
        flash('err', `Please select parent ${TYPE_LABEL[PARENT_TYPE[type]] || 'location'}`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        Id: Number(locForm.Id) || 0,
        WarehouseId: Number(locForm.WarehouseId),
        ParentId: parentId,
        Code: locForm.Code || '',
        Name: locForm.Name.trim(),
        LocationType: type,
        SortOrder: Number(locForm.SortOrder) || 0,
      };

      const resp = await MainService.SaveWarehouseLocation(payload);
      if (resp === 'Success') {
        setLocOpen(false);
        flash('ok', `${TYPE_LABEL[type] || 'Location'} saved under hierarchy`);
        await loadLocations(payload.WarehouseId);
        setExpanded((prev) => {
          const next = new Set(prev);
          next.add(`wh:${payload.WarehouseId}`);
          if (payload.ParentId) next.add(`loc:${payload.ParentId}`);
          return next;
        });
      } else {
        flash('err', typeof resp === 'string' ? resp : 'Failed to save location');
      }
    } catch (err) {
      flash('err', err?.message || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const deleteLoc = async (loc) => {
    if (!window.confirm(`Delete ${TYPE_LABEL[loc.LocationType] || loc.LocationType} "${loc.Name}"?`)) return;
    try {
      const resp = await MainService.DeleteWarehouseLocation(loc.Id);
      if (resp === 'Success') {
        flash('ok', 'Deleted');
        await loadLocations(loc.WarehouseId);
      } else flash('err', typeof resp === 'string' ? resp : 'Delete failed');
    } catch (err) {
      flash('err', err?.message || 'Delete failed');
    }
  };

  const deleteWarehouse = async (wh) => {
    if (!window.confirm(`Delete warehouse "${wh.Name}"?`)) return;
    try {
      const resp = await MainService.DeleteWarehouse(wh.Id);
      if (resp === 'Success') {
        flash('ok', 'Warehouse deleted');
        setLocMap((prev) => {
          const next = { ...prev };
          delete next[wh.Id];
          return next;
        });
        await load();
      } else flash('err', resp || 'Delete failed');
    } catch (err) {
      flash('err', err?.message || 'Delete failed');
    }
  };

  const trees = useMemo(() => {
    const map = {};
    Object.keys(locMap).forEach((id) => {
      map[id] = buildTree(locMap[id]);
    });
    return map;
  }, [locMap]);

  const parentOptionsForForm = useMemo(() => {
    if (!locForm?.WarehouseId) return [];
    const needed = PARENT_TYPE[locForm.LocationType];
    if (!needed) return [];
    return (locMap[locForm.WarehouseId] || []).filter((l) => l.LocationType === needed);
  }, [locForm, locMap]);

  const renderLocNode = (loc, warehouseId, depth) => {
    const isBin = loc.LocationType === 'BIN';
    const key = `loc:${loc.Id}`;
    const isOpen = expanded.has(key);
    const childType = CHILD_TYPE[loc.LocationType];

    return (
      <div key={loc.Id} className="select-none">
        <div
          className="group flex items-center gap-1 rounded-md py-1.5 pr-2 hover:bg-gray-50"
          style={{ paddingLeft: `${depth * 18 + 8}px` }}
        >
          {isBin ? (
            <span className="inline-flex h-5 w-5 items-center justify-center text-gray-300">•</span>
          ) : (
            <button
              type="button"
              className="inline-flex h-5 w-5 items-center justify-center rounded text-gray-500 hover:bg-gray-200"
              onClick={() => toggleExpand(key, warehouseId)}
            >
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          )}

          <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${TYPE_COLOR[loc.LocationType] || 'bg-gray-100'}`}>
            {loc.LocationType}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
            {loc.Code ? `${loc.Code} — ` : ''}
            {loc.Name}
          </span>

          <div className="flex shrink-0 items-center gap-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100">
            {childType && (
              <button
                type="button"
                title={`Add ${TYPE_LABEL[childType]} under this ${TYPE_LABEL[loc.LocationType]}`}
                className="inline-flex items-center gap-0.5 rounded px-1.5 py-1 text-xs text-primary hover:bg-green-50"
                onClick={() => openAddChild(warehouseId, loc)}
              >
                <Plus className="h-3.5 w-3.5" />
                {TYPE_LABEL[childType]}
              </button>
            )}
            <button type="button" className="rounded px-1.5 py-1 hover:bg-blue-50" onClick={() => openEditLoc(loc)}>
              <Edit className="h-3.5 w-3.5 text-blue-600" />
            </button>
            <button type="button" className="rounded px-1.5 py-1 hover:bg-red-50" onClick={() => deleteLoc(loc)}>
              <Trash2 className="h-3.5 w-3.5 text-red-600" />
            </button>
          </div>
        </div>

        {isOpen &&
          (loc.children?.length
            ? loc.children.map((child) => renderLocNode(child, warehouseId, depth + 1))
            : childType && (
                <div className="py-1 text-xs text-gray-400" style={{ paddingLeft: `${(depth + 1) * 18 + 28}px` }}>
                  No {TYPE_LABEL[childType]}s — click + {TYPE_LABEL[childType]} on the row above
                </div>
              ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-white p-5 shadow-sm">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <FolderTree className="h-6 w-6 text-primary" />
            Warehouse Master
          </h1>
          <p className="mt-1 text-sm text-gray-500">Warehouse → Area → Rack → Shelf → Bin</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              setWhForm(emptyWarehouse());
              setWhOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white"
          >
            <Plus className="h-4 w-4" /> Add Warehouse
          </button>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {success && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{success}</div>
      )}

      <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
        <div className="border-b bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Location hierarchy tree
        </div>

        {loading && rows.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">Loading warehouses…</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">No warehouses yet. Click Add Warehouse to start.</p>
        ) : (
          <div className="divide-y">
            {rows.map((wh) => {
              const whKey = `wh:${wh.Id}`;
              const isOpen = expanded.has(whKey);
              const built = trees[wh.Id] || { tree: [], orphans: [] };
              const loadingLocs = locLoadingId === wh.Id;

              return (
                <div key={wh.Id}>
                  <div className="group flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50">
                    <button
                      type="button"
                      className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-600 hover:bg-gray-200"
                      onClick={() => toggleExpand(whKey, wh.Id)}
                    >
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <WarehouseIcon className="h-4 w-4 text-primary" />
                    <button type="button" className="min-w-0 flex-1 text-left" onClick={() => toggleExpand(whKey, wh.Id)}>
                      <div className="truncate text-sm font-semibold text-gray-900">
                        {wh.Code ? `${wh.Code} — ` : ''}
                        {wh.Name}
                      </div>
                      <div className="text-xs text-gray-500">
                        Warehouse{wh.BranchCode ? ` · Branch ${wh.BranchCode}` : ''}
                      </div>
                    </button>
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        title="Add Area under this warehouse"
                        className="inline-flex items-center gap-1 rounded border border-primary/30 bg-primary/5 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
                        onClick={() => openAddChild(wh.Id, null)}
                      >
                        <Plus className="h-3.5 w-3.5" /> Area
                      </button>
                      <button
                        type="button"
                        className="rounded px-1.5 py-1 hover:bg-blue-50"
                        onClick={() => {
                          setWhForm({ ...emptyWarehouse(), ...wh });
                          setWhOpen(true);
                        }}
                      >
                        <Edit className="h-3.5 w-3.5 text-blue-600" />
                      </button>
                      <button type="button" className="rounded px-1.5 py-1 hover:bg-red-50" onClick={() => deleteWarehouse(wh)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-600" />
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t border-dashed bg-slate-50/60 pb-2 pt-1">
                      {loadingLocs ? (
                        <p className="px-10 py-2 text-xs text-gray-500">Loading locations…</p>
                      ) : (
                        <>
                          {built.tree.length === 0 ? (
                            <p className="px-10 py-2 text-xs text-gray-500">
                              No areas yet. Click <strong>+ Area</strong> on the warehouse.
                            </p>
                          ) : (
                            built.tree.map((loc) => renderLocNode(loc, wh.Id, 1))
                          )}

                          {built.orphans.length > 0 && (
                            <div className="mx-4 mt-2 rounded border border-amber-200 bg-amber-50 p-2">
                              <p className="mb-1 text-xs font-semibold text-amber-800">
                                Unassigned (not under Area → Rack → Shelf → Bin) — edit and set parent
                              </p>
                              {built.orphans.map((loc) => (
                                <div key={`orphan-${loc.Id}`} className="flex items-center gap-2 py-1 text-sm">
                                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${TYPE_COLOR[loc.LocationType]}`}>
                                    {loc.LocationType}
                                  </span>
                                  <span className="flex-1">
                                    {loc.Code ? `${loc.Code} — ` : ''}
                                    {loc.Name}
                                  </span>
                                  <button
                                    type="button"
                                    className="rounded border px-2 py-0.5 text-xs text-blue-700"
                                    onClick={() => openEditLoc(loc)}
                                  >
                                    Set parent
                                  </button>
                                  <button type="button" onClick={() => deleteLoc(loc)}>
                                    <Trash2 className="h-3.5 w-3.5 text-red-600" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {whOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form onSubmit={saveWarehouse} className="w-full max-w-lg space-y-3 rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{whForm.Id ? 'Edit' : 'New'} Warehouse</h3>
              <button type="button" onClick={() => setWhOpen(false)}>
                <X />
              </button>
            </div>
            {['Code', 'Name', 'NameAr', 'BranchCode', 'Address', 'ContactMobile', 'LAT', 'LNG'].map((f) => (
              <input
                key={f}
                className="w-full rounded border px-3 py-2 text-sm"
                placeholder={f}
                required={f === 'Name'}
                value={whForm[f] || ''}
                onChange={(e) => setWhForm((p) => ({ ...p, [f]: e.target.value }))}
              />
            ))}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setWhOpen(false)} className="rounded border px-3 py-2 text-sm">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-sm text-white disabled:opacity-60"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {locOpen && locForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form onSubmit={saveLoc} className="w-full max-w-md space-y-3 rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {locForm.Id ? 'Edit' : 'Add'} {TYPE_LABEL[locForm.LocationType] || locForm.LocationType}
              </h3>
              <button type="button" onClick={() => setLocOpen(false)}>
                <X />
              </button>
            </div>

            {locForm.LocationType !== 'AREA' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Parent {TYPE_LABEL[PARENT_TYPE[locForm.LocationType]]} *
                </label>
                <select
                  className="w-full rounded border px-3 py-2 text-sm"
                  required
                  value={locForm.ParentId ?? ''}
                  onChange={(e) =>
                    setLocForm((p) => ({
                      ...p,
                      ParentId: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                >
                  <option value="">Select {TYPE_LABEL[PARENT_TYPE[locForm.LocationType]]}…</option>
                  {parentOptionsForForm.map((p) => (
                    <option key={p.Id} value={p.Id}>
                      {p.Code ? `${p.Code} — ` : ''}
                      {p.Name}
                    </option>
                  ))}
                </select>
                {parentOptionsForForm.length === 0 && (
                  <p className="mt-1 text-xs text-red-600">
                    No {TYPE_LABEL[PARENT_TYPE[locForm.LocationType]]} exists yet. Create that level first.
                  </p>
                )}
              </div>
            )}

            <input
              className="w-full rounded border px-3 py-2 text-sm"
              placeholder="Code (optional)"
              value={locForm.Code}
              onChange={(e) => setLocForm((p) => ({ ...p, Code: e.target.value }))}
            />
            <input
              className="w-full rounded border px-3 py-2 text-sm"
              placeholder="Name *"
              required
              autoFocus
              value={locForm.Name}
              onChange={(e) => setLocForm((p) => ({ ...p, Name: e.target.value }))}
            />
            <input
              type="number"
              className="w-full rounded border px-3 py-2 text-sm"
              placeholder="Sort order"
              value={locForm.SortOrder}
              onChange={(e) => setLocForm((p) => ({ ...p, SortOrder: Number(e.target.value) || 0 }))}
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setLocOpen(false)} className="rounded border px-3 py-2 text-sm">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-sm text-white disabled:opacity-60"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default WarehouseMaster;
